import { ADMIN_COLLECTION_KEYS as ADMIN_COLLECTION_KEYS$1, ADMIN_PROPERTY_KEYS as ADMIN_PROPERTY_KEYS$1, nestAdminCollectionKeys, resolveResourceRefs } from "@rebasepro/types";
//#region src/types/property_options.ts
/**
* Core's list, re-exported through the same `satisfies` agreement check that
* {@link ADMIN_COLLECTION_KEYS} gets: core owns the data because the boot-time
* collection validator in `@rebasepro/server` needs it and may not import this
* package, and this clause is what stops the data from drifting off the types.
*/
var ADMIN_PROPERTY_KEYS = ADMIN_PROPERTY_KEYS$1;
//#endregion
//#region src/react_component_ref.ts
/**
* The `ComponentLike` contract, as a signature the compiler has to keep true.
*
* The split rests on one claim: **every form a React component takes is
* assignable to `ComponentLike`** — function components, class components,
* `memo`, `forwardRef`. If that stopped holding, core's `ComponentRef` would
* quietly begin rejecting real components, and the failure would surface far away
* in whichever collection file happened to use the broken form.
*
* So the claim is not left to a test that someone has to run. This function's
* parameter and return types state it, and `pnpm typecheck` enforces it on every
* commit. It is also useful on its own: an explicit widening at the point where
* an authored component enters a collection config.
*
* @example
* import { MyField } from "./MyField";
* admin: { Field: asComponentRef(MyField) }
*/
function asComponentRef(component) {
	return component;
}
/**
* The same contract in the other direction: a `ComponentLike` is only renderable
* once narrowed, and this is the single sanctioned place that narrowing is
* spelled out. `resolveComponentRef` in `@rebasepro/app` does the runtime half.
*/
function asReactComponent(component) {
	return component;
}
//#endregion
//#region src/define_collection.ts
/**
* At runtime this records the collection as data: a resource handle written
* where a key belongs — `dataSource: analytics`, `storageSource: media` — is
* replaced by its key, so what leaves here serialises and compares like the
* string it always was. The signature above is the rest of the point.
* @group Builder
*/
function defineCollection(collection) {
	return resolveResourceRefs(collection);
}
//#endregion
//#region src/admin_collection.ts
/**
* Core's list, re-exported through a `satisfies` clause that is the agreement
* check: a key core names that is not an option here fails to compile, and
* `satisfies` keeps the literal tuple type rather than widening it to `string[]`.
*/
var ADMIN_COLLECTION_KEYS = ADMIN_COLLECTION_KEYS$1;
/**
* Flatten a collection's `admin` block onto it, producing the panel's view model.
*
* Shallow by design: the block's fields are independent, so a deep merge would
* only create opportunities for a nested object to be half from one source and
* half from the other. `admin` survives on the result.
*
* Idempotent — flattening an already-flat collection returns an equivalent one —
* because the panel resolves collections at more than one entry point (the
* registry, `<Rebase collections>`, a plugin's `modifyCollection`) and they must
* not fight over which has run.
*/
function resolveAdminCollection(collection) {
	const block = collection.admin;
	if (!block) return collection;
	return {
		...collection,
		...block,
		admin: block
	};
}
/**
* The inverse: lift flattened admin fields back into the block.
*
* Used on the way out — persisting from the collection editor, or handing a
* collection to anything that expects the authoring shape. Any key in
* {@link ADMIN_COLLECTION_KEYS} found at the top level is moved down, so a
* round trip through the panel does not leave the file flat.
*
* The nesting itself lives in `@rebasepro/types` because the schema editor in
* `@rebasepro/server` — which cannot import this package — has to do exactly the
* same thing when it writes a collection file back to disk. Two copies of the
* rule disagreed on which side wins, and the disagreement was invisible.
*/
function toAdminCollectionConfig(collection) {
	return nestAdminCollectionKeys(collection);
}
//#endregion
//#region src/types/entity_display.ts
/**
* The roles as data, so every consumer iterates the same list instead of
* repeating it — the mistake that let `titleProperty` grow seven readers.
*/
var ENTITY_DISPLAY_ROLES = [
	"title",
	"subtitle",
	"image",
	"status",
	"date",
	"tags"
];
//#endregion
//#region src/types/form_layout.ts
/**
* The number of columns the form grid is divided into. A field's
* {@link AdminPropertyOptions.span} is expressed against this.
*
* Fixed rather than configurable on purpose: the whole point of a span is that
* two fields written by two different people line up, and they only do that if
* everyone is counting against the same grid.
*
* @group Models
*/
var FORM_GRID_COLUMNS = 4;
//#endregion
//#region src/types/slots.tsx
/**
* Slots this build declares but renders nowhere. **Empty, and meant to stay so.**
*
* It held seven of twenty-nine. Each appeared in {@link SlotRegistry}, had a
* props interface, and had a row in the public slot reference alongside the
* ones that work — so a plugin author picked one off the table, registered a
* component, saw nothing, and had no way to tell whether the fault was theirs.
*
* Five were implemented: `entity.field.before` and `entity.field.after` render
* in `PropertyFieldBinding` (the one component every form field goes through),
* `entity.row.actions` in `CollectionRowActions`, and `global.search` and
* `shell.toolbar` in `DefaultAppBar`. Two were removed, because nothing was
* missing that a declared slot would have supplied:
*
* - **`collection.filter-panel`** described a filter sidebar the admin does not
*   have. Rendering it meant inventing a region, which is a feature and not a
*   slot; `collection.toolbar` and `collection.widgets` are the declared places
*   for filter UI beside a table, and both work.
* - **`dashboard.widget`** took `{ context }` and nothing else, so it carried
*   no position on a page that already has four positions —
*   `home.children.start`, `home.children.end`, `home.cards` and
*   `home.card.widget`.
*
* This is a statement of fact, not a wish list: `slot-render-sites.test.ts`
* derives the same set by scanning for render sites and fails when the two
* disagree — in *both* directions. So a slot declared without a render site
* has to be added back here, at which point `Rebase` warns anyone who registers
* for it, which is the whole point.
*/
var UNRENDERED_SLOTS = [];
//#endregion
export { ADMIN_COLLECTION_KEYS, ADMIN_PROPERTY_KEYS, ENTITY_DISPLAY_ROLES, FORM_GRID_COLUMNS, UNRENDERED_SLOTS, asComponentRef, asReactComponent, defineCollection, resolveAdminCollection, toAdminCollectionConfig };

//# sourceMappingURL=index.es.js.map